/**
 * Build Configuration Mapping Layer
 * 
 * Single source of truth for deriving display data, pricing inputs,
 * and scope from a ProjectConfiguration. All downstream consumers
 * (presentation, contracts, jobs, change orders) MUST use these
 * functions instead of inline derivation.
 */

import type { ProjectConfiguration } from "./types";
import type { ProductOption } from "./ProductOptionsContext";
import { calculatePrice, type ComplexityFactors, type PriceBreakdown, type PricingOverrides } from "./pricingEngine";
import { generateScopeOfWork } from "./scopeOfWork";

function normalizeConfigArrays(config: ProjectConfiguration) {
  return {
    bathroomMaterials: config.bathroomMaterials ?? [],
    selectedGrabBars: config.selectedGrabBars ?? [],
    selectedAddOns: config.selectedAddOns ?? [],
    customQuoteItems: config.customQuoteItems ?? [],
  };
}

/* ═══════════════════════════════════════════════════
   Build Summary Item — homeowner-friendly display
   ═══════════════════════════════════════════════════ */

export interface BuildSummaryItem {
  label: string;
  value: string;
  /** Category for grouping: core | accessory | addon */
  category: "core" | "accessory" | "addon";
}

/**
 * Derives a clean, homeowner-friendly build summary from project configuration.
 * Used by: Presentation Mode, Contract, Job Detail, Quote Summary.
 */
export function deriveBuildSummary(
  config: ProjectConfiguration,
  allOptions: ProductOption[],
): BuildSummaryItem[] {
  const items: BuildSummaryItem[] = [];
  const optName = (id: string) => allOptions.find(o => o.id === id)?.displayName;
  const {
    bathroomMaterials,
    selectedGrabBars,
    selectedAddOns,
  } = normalizeConfigArrays(config);

  // Core selections
  if (config.bathroomSystemType) {
    items.push({ label: "System", value: config.bathroomSystemType, category: "core" });
  }
  if (bathroomMaterials.length) {
    items.push({ label: "Material", value: bathroomMaterials.join(", "), category: "core" });
  }
  if (config.baseWidth && config.baseLength) {
    items.push({ label: "Base Size", value: `${config.baseWidth}" × ${config.baseLength}"`, category: "core" });
  }
  if (config.baseType) {
    items.push({ label: "Base Type", value: config.baseType, category: "core" });
  }
  if (config.baseStyle) {
    items.push({ label: "Base Style", value: config.baseStyle, category: "core" });
  }

  // Build components
  if (config.selectedWallSystem) {
    const n = optName(config.selectedWallSystem);
    if (n) items.push({ label: "Wall System", value: n, category: "core" });
  }
  if (config.selectedGlass) {
    const n = optName(config.selectedGlass);
    if (n) items.push({ label: "Glass", value: n, category: "core" });
  }
  if (config.selectedDoor) {
    const n = optName(config.selectedDoor);
    if (n) items.push({ label: "Door", value: n, category: "core" });
  }
  if (config.selectedFixture) {
    const n = optName(config.selectedFixture);
    if (n) items.push({ label: "Fixtures", value: n, category: "accessory" });
  }

  // Accessories & add-ons
  if (selectedGrabBars.length > 0) {
    items.push({
      label: "Grab Bars",
      value: selectedGrabBars.map(id => optName(id) || id).join(", "),
      category: "accessory",
    });
  }
  if (selectedAddOns.length > 0) {
    items.push({
      label: "Add-ons",
      value: selectedAddOns.map(id => optName(id) || id).join(", "),
      category: "addon",
    });
  }

  // Color selections
  if (config.wallColor) {
    items.push({ label: "Wall Color", value: config.wallColor, category: "core" });
  }
  if (config.baseColor) {
    items.push({ label: "Base Color", value: config.baseColor, category: "core" });
  }
  if (config.colorSelection && !config.wallColor && !config.baseColor) {
    items.push({ label: "Color Selection", value: config.colorSelection, category: "core" });
  }

  // Enclosure & hardware
  if (config.enclosureType) {
    items.push({ label: "Enclosure", value: config.enclosureType, category: "core" });
  }
  if (config.hardwareFinish) {
    items.push({ label: "Hardware Finish", value: config.hardwareFinish, category: "accessory" });
  }

  return items;
}

/* ═══════════════════════════════════════════════════
   Pricing derivation — always from config
   ═══════════════════════════════════════════════════ */

export interface DerivedPricing {
  breakdown: PriceBreakdown;
  standardPrice: number;
  promoPrice: number;
  ivpCashPrice: number;
  ivpFinancePrice: number;
}

/**
 * Derives all pricing tiers from the core build configuration.
 * All pricing views MUST use this instead of inline calculation.
 */
export function derivePricing(
  config: ProjectConfiguration,
  allOptions: ProductOption[],
  overrides?: PricingOverrides,
): DerivedPricing {
  const complexity: ComplexityFactors = {
    laborComplexity: config.laborComplexity || "standard",
    plumbingRelocation: config.plumbingRelocation || false,
    customModifications: config.customModifications || false,
  };
  const manualAdj = parseFloat(config.manualAdjustment || "0") || 0;
  const breakdown = calculatePrice(config, allOptions, complexity, manualAdj, config.manualAdjustmentNotes || "", overrides);

  return {
    breakdown,
    standardPrice: breakdown.finalTotal,
    promoPrice: Math.round(breakdown.finalTotal * (1 - 0.17)),
    ivpCashPrice: Math.round(Math.round(breakdown.finalTotal * (1 - 0.17)) * (1 - 0.12)),
    ivpFinancePrice: Math.round(Math.round(breakdown.finalTotal * (1 - 0.17)) * (1 - 0.07)),
  };
}

/* ═══════════════════════════════════════════════════
   Scope of Work derivation — always from config
   ═══════════════════════════════════════════════════ */

export interface DerivedScope {
  summary: string;
  sections: { title: string; items: string[] }[];
}

/**
 * Derives scope of work from the core build configuration.
 */
export function deriveScope(config: ProjectConfiguration): DerivedScope {
  return generateScopeOfWork(config);
}

/* ═══════════════════════════════════════════════════
   Contract data derivation — always from config
   ═══════════════════════════════════════════════════ */

export interface DerivedContractData {
  projectDescription: string;
  buildSummary: BuildSummaryItem[];
  scope: DerivedScope;
  contractTotal: number;
}

/**
 * Derives all contract-ready data from the core configuration.
 * Ensures contract content exactly matches what was configured.
 */
export function deriveContractData(
  config: ProjectConfiguration,
  allOptions: ProductOption[],
  overrides?: PricingOverrides,
): DerivedContractData {
  const { bathroomMaterials } = normalizeConfigArrays(config);
  const buildSummary = deriveBuildSummary(config, allOptions);
  const scope = deriveScope(config);
  const pricing = derivePricing(config, allOptions, overrides);

  const projectDescription = [
    config.bathroomSystemType,
    bathroomMaterials.length ? `(${bathroomMaterials.join(", ")})` : "",
    config.baseWidth && config.baseLength ? `${config.baseWidth}" × ${config.baseLength}"` : "",
  ].filter(Boolean).join(" — ");

  return {
    projectDescription,
    buildSummary,
    scope,
    contractTotal: pricing.standardPrice,
  };
}

/* ═══════════════════════════════════════════════════
   Job derivation — fields from config
   ═══════════════════════════════════════════════════ */

/**
 * Derives job-level fields from the core build configuration.
 * Prevents data drift between Quote and Job.
 */
export function deriveJobFields(config: ProjectConfiguration) {
  return {
    projectType: config.selectedVerticals?.join(", ") || "General",
    scopeSummary: config.workDescription || deriveScope(config).summary || "",
  };
}

/* ═══════════════════════════════════════════════════
   Config snapshot — for immutability tracking
   ═══════════════════════════════════════════════════ */

/**
 * Creates a frozen snapshot of the build configuration
 * for attachment to contracts and change orders.
 */
export function createConfigSnapshot(config: ProjectConfiguration): {
  config: ProjectConfiguration;
  snapshotAt: string;
  hash: string;
} {
  const snapshot = JSON.parse(JSON.stringify(config));
  const hash = simpleHash(JSON.stringify(snapshot));
  return {
    config: snapshot,
    snapshotAt: new Date().toISOString(),
    hash,
  };
}

/**
 * Verifies a config snapshot matches the current configuration.
 */
export function verifyConfigIntegrity(
  currentConfig: ProjectConfiguration,
  snapshotHash: string,
): boolean {
  return simpleHash(JSON.stringify(currentConfig)) === snapshotHash;
}

/** Simple deterministic hash for config comparison */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return `cfg-${Math.abs(hash).toString(36)}`;
}
